import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, EmptyState, FormActions, FormSection, PageHeader } from '../../../ui';
import StepperVistoria from '../../../components/vistoria/StepperVistoria';

import InspectionCapture from '../../../components/inspection/InspectionCapture';
import {
  InspectionImageMeta,
  InspectionImageWithFile,
} from '../../../components/inspection/types';
import MiniaturaFotoVistoria from '../../../components/vistoria/MiniaturaFotoVistoria';
import ModalConfirmacao from '../../../components/modal/confirmacao/ModalConfirmacao';
import AvisoDivergenciaSga from '../../../components/divergencia-sga/AvisoDivergenciaSga';
import VistoriaUploadProgress from '../../../components/vistoria-progress/VistoriaUploadProgress';
import { reconciliarVeiculo } from '../../../api/sga-reconciliacao/sgaReconciliacaoApi';
import { DivergenciaSga } from '../../../api/sga-divergencia/sgaDivergenciaApi';
import {
  AcaoSincronizacaoSga,
  mensagemDeErroSincronizacao,
  sincronizarVeiculoComSga,
} from '../../../utils/sincronizacaoSga';
import { RootState } from '../../../store/store';
import {
  addImage,
  resetInspection,
  setCpf,
  setVehicle,
  setVistoriaId,
} from '../../../store/vistoriaSlice';
import { extrairMensagemErro } from '../../../utils/apiError';
import { legendasPorTipo } from '../../../utils/vistoriaLegendas';
import { VISTORIA, VISTORIA_CHECKLIST_ACESSORIOS, VISTORIA_LAUDO } from '../routes';

import {
  buscarVistoriaPorId,
  criarVistoria,
  removerFotoVistoria,
  uploadVistoriaPhotos,
} from '../../../api/vistoria/vistoriaApi';

/**
 * `/vistoria/captura` — **etapa 1 do fluxo de vistoria** (reescrita na at-63,
 * frente D/D2).
 *
 * 🔴 **O motor de captura NÃO foi tocado.** `useInspectionCapture`, `utils.ts`,
 * `types.ts` e a lógica de traços do `MarcarAvariaCanvas` são o trecho mais
 * dependente de aparelho do app e o único que não se exercita em teste
 * automatizado: `getUserMedia`, as esperas com timeout pelo `<video>`, a legenda
 * **queimada** na foto, o `toBlob` JPEG 0.9, a geolocalização e o **modelo de
 * slots** (bug B6 da at-24). O que mudou nesta branch foi o JSX e o CSS ao redor.
 *
 * 🔴 **O upload sequencial com retry foi preservado linha a linha** — uma
 * requisição por foto, `statusFotos`, o peso do corpo HTTP dentro do degrau,
 * `resetInspection` só na PRIMEIRA tentativa, `break` na primeira falha e o texto
 * do botão por contexto. É a barra de progresso honesta da at-24, e ela só é
 * honesta porque cada resposta significa foto no GCS **e** persistida.
 *
 * ## O que mudou de comportamento
 *
 *   1. 🐛 **Fim do `navigate()` DURANTE o render.** A tela fazia
 *      `if (!vistoria.vehicle || !vistoria.cpf) navigate(VISTORIA())` no corpo do
 *      componente — efeito colateral no render, que o React avisa e que produz um
 *      quadro em branco antes de redirecionar. Abrir a rota por link direto ou dar
 *      F5 caía aí. Agora é `EmptyState` dizendo o que aconteceu, com o caminho de
 *      volta ao painel (mesmo desenho da at-60 em `/assinatura`).
 *   2. **A tela diz em que etapa o consultor está** (`Stepper` "Etapa 1 de 4"),
 *      que é o defeito de fundo destas telas: são 4 etapas em sequência e nada
 *      informava quantas faltam.
 *   3. **`isAdmin` → `userType`.** A flag é legada (`01-identity.md`: *"ao tocar em
 *      código que consulte `isAdmin`, migrar a checagem para `userType`"*). O
 *      resultado visível para TI/ADMIN é o mesmo; muda a fonte da checagem.
 *   4. **A grade de fotos já enviadas usa a miniatura compartilhada** — a avaria
 *      era anunciada por um `::before` de CSS com o texto `'AVARIA · '`, invisível
 *      para quem lê o DOM.
 *
 * ⚠️ **A tela serve DOIS caminhos**: o fluxo novo (vai ao checklist) e a seção
 * "Editar" do laudo (`origemEdicao`, at-24 — volta ao laudo). Sem isso, corrigir
 * uma foto arrastava o consultor pelo fluxo inteiro e forçava uma NOVA assinatura.
 */

/**
 * Estado de cada foto no envio. As fotos sobem uma requisição por vez (o endpoint
 * `POST /vistorias/:id/photos` faz merge por id), então "concluida" significa
 * foto já no GCS E persistida no banco — é isso que alimenta o progresso real.
 */
type StatusFoto = 'pendente' | 'enviando' | 'concluida' | 'falhou';

/** Peso da fração do corpo HTTP da foto corrente dentro do "degrau" dela na barra. */
const PESO_CORPO_FOTO_ATUAL = 0.5;

/** Par rótulo/valor do bloco do veículo — eram `<b>` soltos numa pilha de `<div>`. */
const Dado: React.FC<{ rotulo: string; valor?: React.ReactNode }> = ({ rotulo, valor }) => (
  <div className="tw-min-w-0">
    <dt className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
      {rotulo}
    </dt>
    <dd className="tw-m-0 tw-truncate tw-text-sm tw-font-semibold tw-text-texto-corpo">
      {valor || '—'}
    </dd>
  </div>
);

const VistoriaCapturaPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const voltar = useVoltar(VISTORIA());

  /**
   * Tela aberta pela secao "Editar" do laudo (at-24): ao terminar, volta para o
   * laudo em vez de seguir para o checklist. Sem isso, corrigir uma foto arrastava
   * o consultor pelo fluxo inteiro e forcava uma NOVA assinatura.
   */
  const origemEdicao = (location.state as any)?.origemEdicao === true;
  const proximaTela = () =>
    origemEdicao ? VISTORIA_LAUDO() : VISTORIA_CHECKLIST_ACESSORIOS();

  const vistoria = useSelector((state: RootState) => state.vistoria);
  /**
   * Seletor de arquivos: liberado para TI/ADMIN.
   *
   * ⚠️ Era `state.auth.isAdmin` — flag **legada** que nenhuma implementação nova
   * deve ler (`01-identity.md`). O critério equivalente no frontend é o do
   * `PendenciasBanner`: `userType` em TI/ADMIN.
   */
  const userType = useSelector((state: RootState) => state.auth.userType);
  const elevado = userType === 'TI' || userType === 'ADMIN';

  /*
   * ⚠️ Só o SETTER é guardado, de propósito: `onImagesChange` é prop obrigatória
   * do `InspectionCapture` e nada nesta tela lê as metas (quem envia usa
   * `imagesWithFile`, que traz o `File`). Guardar o valor rendia o aviso
   * "'imagesMeta' is assigned a value but never used" desde antes desta branch.
   */
  const [, setImagesMeta] = useState<InspectionImageMeta[]>([]);
  const [imagesWithFile, setImagesWithFile] = useState<
    InspectionImageWithFile[]
  >([]);
  const [saving, setSaving] = useState(false);
  /**
   * Reconciliação on-access (at-32): este é o início do fluxo de vistoria — uma
   * chamada por VEÍCULO, não por linha do painel. Avisar aqui é o que evita o
   * consultor tirar 29 fotos de um veículo que já foi substituído no SGA. O
   * aviso não bloqueia nada: se o SGA estiver fora do ar, nem aparece.
   */
  const [divergenciasSga, setDivergenciasSga] = useState<DivergenciaSga[]>([]);
  const [sincronizandoSga, setSincronizandoSga] = useState(false);
  /**
   * Vistoria já existente no backend — reaproveitada para NUNCA criar uma segunda.
   *
   * Inicializa com o `vistoriaId` do Redux (at-24): ao retomar um rascunho, esta tela
   * ignorava esse valor e criava outra vistoria, deixando a primeira órfã com as
   * fotos dentro. Só cria quando realmente não existe nenhuma.
   */
  const [vistoriaIdCriada, setVistoriaIdCriada] = useState<string | null>(
    vistoria.vistoriaId ?? null
  );
  /** Fotos que já estão no banco (rascunho retomado), com o id para remoção. */
  const [fotosEnviadas, setFotosEnviadas] = useState<
    Array<{ id: string; url: string; caption: string; damaged?: boolean }>
  >([]);
  const [removendoFoto, setRemovendoFoto] = useState<string | null>(null);
  /** Foto aguardando confirmacao de remocao (modal, sem window.confirm). */
  const [fotoParaRemover, setFotoParaRemover] = useState<{ id: string; caption: string } | null>(null);
  const [statusFotos, setStatusFotos] = useState<Record<string, StatusFoto>>({});
  const [fracaoFotoAtual, setFracaoFotoAtual] = useState(0);
  const [envioFalhou, setEnvioFalhou] = useState(false);

  /**
   * Carrega as fotos já enviadas quando se retoma um rascunho (at-24). Elas viram
   * "slots preenchidos": não são recapturadas, e podem ser removidas para correção.
   */
  // Fire-and-forget: dispara ao abrir e some do caminho. A função de API nunca
  // lança, então SGA indisponível simplesmente não produz aviso.
  useEffect(() => {
    const carroId = vistoria.vehicle?.id;
    if (!carroId) return;

    let ativo = true;
    reconciliarVeiculo(String(carroId)).then((resultado) => {
      if (ativo) setDivergenciasSga(resultado.divergencias);
    });

    return () => {
      ativo = false;
    };
  }, [vistoria.vehicle?.id]);

  useEffect(() => {
    const id = vistoria.vistoriaId;
    if (!id) return;

    let ativo = true;

    buscarVistoriaPorId({ vistoriaId: id })
      .then((resp) => {
        if (!ativo) return;

        const fotos = Array.isArray(resp?.fotos) ? resp.fotos : [];
        setFotosEnviadas(
          fotos
            .filter((f) => !!f?.url && !!f?.id)
            .map((f) => ({
              id: String(f.id),
              url: String(f.url),
              caption: String(f.caption ?? ''),
              damaged: f.damaged === true,
            }))
        );
      })
      .catch(() => {
        // Sem as fotos existentes a tela ainda funciona (trataria tudo como novo);
        // o merge por id no backend evita duplicar quem já está lá.
      });

    return () => {
      ativo = false;
    };
  }, [vistoria.vistoriaId]);

  const confirmarRemocaoFoto = async () => {
    const foto = fotoParaRemover;
    const id = vistoria.vistoriaId ?? vistoriaIdCriada;
    if (!foto || !id) return;

    setRemovendoFoto(foto.id);
    try {
      await removerFotoVistoria({ vistoriaId: id, fotoId: foto.id });
      setFotosEnviadas((prev) => prev.filter((f) => f.id !== foto.id));
      toast.success(`Foto "${foto.caption}" removida. Capture novamente para preenchê-la.`);
      setFotoParaRemover(null);
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível remover a foto.'));
    } finally {
      setRemovendoFoto(null);
    }
  };

  const handleEnviarVistoria = async () => {
    if (!vistoria.vehicle || !vistoria.cpf) {
      navigate(VISTORIA());
      return;
    }

    if (!vistoria.vehicle.id) {
      toast.error(
        'Veículo sem identificação. Volte e busque o veículo novamente.'
      );
      return;
    }

    // Só envia com TODOS os slots preenchidos — contando as fotos que já estão no
    // banco (rascunho retomado) mais as novas desta sessão.
    if (fotosEnviadas.length + imagesWithFile.length !== captions.length) return;

    // Snapshot do que ainda falta: no retry, as fotos já concluídas são puladas.
    const pendentes = imagesWithFile.filter(
      (img) => statusFotos[img.meta.id] !== 'concluida'
    );
    if (pendentes.length === 0) {
      navigate(proximaTela());
      return;
    }

    setSaving(true);
    setEnvioFalhou(false);
    setFracaoFotoAtual(0);
    setStatusFotos((anterior) => {
      const proximo: Record<string, StatusFoto> = {};
      imagesWithFile.forEach(({ meta }) => {
        proximo[meta.id] =
          anterior[meta.id] === 'concluida' ? 'concluida' : 'pendente';
      });
      return proximo;
    });

    let falhou = false;

    try {
      let vistoriaId = vistoriaIdCriada;

      if (!vistoriaId) {
        // Primeira tentativa: hidrata o estado do fluxo no Redux (comportamento anterior).
        // No retry esse bloco não roda — o Redux já está hidratado e o resetInspection
        // apagaria o vistoriaId/imagens que acabaram de ser gravados.
        dispatch(resetInspection());
        dispatch(setCpf(vistoria.cpf));
        dispatch(setVehicle(vistoria.vehicle));

        imagesWithFile.forEach((img) =>
          dispatch(addImage({ ...img.meta, previewUrl: img.previewUrl }))
        );

        const created = await criarVistoria({
          carroId: vistoria.vehicle.id,
        });

        vistoriaId = created.id;
        setVistoriaIdCriada(created.id);
        dispatch(setVistoriaId(created.id));
      }

      const idVistoria = vistoriaId;

      // Uma requisição por foto, sequencialmente: cada resposta significa foto no GCS
      // e persistida — é o progresso verdadeiro. Sequencial também é mais previsível
      // em rede móvel e preserva a ordem das fotos.
      for (const imagem of pendentes) {
        setStatusFotos((anterior) => ({
          ...anterior,
          [imagem.meta.id]: 'enviando',
        }));
        setFracaoFotoAtual(0);

        try {
          await uploadVistoriaPhotos({
            vistoriaId: idVistoria,
            files: [imagem.file],
            metas: [imagem.meta],
            onProgress: (p) => {
              if (p.total) {
                setFracaoFotoAtual(Math.max(0, Math.min(1, p.loaded / p.total)));
              }
            },
          });

          setStatusFotos((anterior) => ({
            ...anterior,
            [imagem.meta.id]: 'concluida',
          }));
          setFracaoFotoAtual(0);
        } catch (erro) {
          setStatusFotos((anterior) => ({
            ...anterior,
            [imagem.meta.id]: 'falhou',
          }));
          falhou = true;
          toast.error(
            extrairMensagemErro(
              erro,
              'Falha ao enviar as fotos. Verifique a conexão e tente novamente.'
            )
          );
          break;
        }
      }
    } catch (erro) {
      // Falha ao criar a vistoria: nenhuma foto foi enviada.
      falhou = true;
      toast.error(
        extrairMensagemErro(
          erro,
          'Não foi possível iniciar a vistoria. Tente novamente.'
        )
      );
    } finally {
      setSaving(false);
      setFracaoFotoAtual(0);
    }

    if (falhou) {
      setEnvioFalhou(true);
      return;
    }

    navigate(proximaTela());
  };

  const tipoDescricao = vistoria.vehicle?.tipoVeiculo ?? '';
  const captions = legendasPorTipo(tipoDescricao);

  const totalCaption = captions.length;

  /**
   * Modelo de SLOTS (at-24): cada legenda é um slot. Um slot já pode estar preenchido
   * por foto no banco (rascunho retomado) — essas não são recapturadas. As legendas
   * ainda livres são o que a captura precisa preencher, e é isso que vai ao
   * `InspectionCapture`: ele passa a receber SÓ os slots vazios, então cada nova foto
   * nasce com a legenda certa mesmo depois de uma remoção no meio.
   */
  const legendasEnviadas = new Set(fotosEnviadas.map((f) => f.caption));
  const legendasVazias = captions.filter((c) => !legendasEnviadas.has(c));
  const currentCount = fotosEnviadas.length + imagesWithFile.length;
  const canSend = currentCount === totalCaption;
  const sendValidationMessage = !canSend
    ? currentCount < totalCaption
      ? `Faltam ${totalCaption - currentCount} fotos para concluir.`
      : `Você tem ${currentCount} fotos, o máximo é ${totalCaption}.`
    : null;

  // Progresso honesto: cada foto só conta como feita quando o backend responde
  // (arquivo no GCS + Json atualizado). A fração do corpo HTTP da foto corrente entra
  // com peso parcial, apenas para dar suavidade dentro do degrau — assim a barra
  // nunca chega a 100% antes da última resposta.
  const totalFotos = imagesWithFile.length;
  const fotosConcluidas = imagesWithFile.filter(
    (img) => statusFotos[img.meta.id] === 'concluida'
  ).length;
  /**
   * Fotos capturadas nesta sessão que **ainda não estão no banco**.
   *
   * Serve a dois propósitos com o mesmo número: o texto do retry ("faltam N") e
   * a guarda de navegação do `StepperVistoria` — elas vivem em estado local, e
   * sair da tela (ou um F5) as descarta.
   */
  const fotosFaltando = totalFotos - fotosConcluidas;
  const percentUpload =
    totalFotos > 0
      ? Math.min(
          100,
          ((fotosConcluidas + fracaoFotoAtual * PESO_CORPO_FOTO_ATUAL) /
            totalFotos) *
            100
        )
      : 0;

  const mostrarProgresso = saving || envioFalhou;
  const rotuloProgresso = saving
    ? `Enviando foto ${Math.min(fotosConcluidas + 1, totalFotos)} de ${totalFotos}...`
    : `${fotosFaltando} de ${totalFotos} fotos não foram enviadas.`;
  const detalheProgresso = envioFalhou
    ? 'As fotos já enviadas foram preservadas. Toque em "Tentar novamente" para enviar só as que faltam.'
    : undefined;

  // O rótulo depende do contexto: no fluxo novo o botão de fato ENVIA a vistoria e
  // avança para o checklist; na edição ele só SALVA as imagens e volta ao laudo.
  // Chamar de "Enviar Vistoria" na edição sugeria que a vistoria seria submetida
  // outra vez.
  const textoBotaoEnvio = saving
    ? origemEdicao
      ? 'Salvando...'
      : 'Enviando...'
    : envioFalhou
    ? `Tentar novamente (faltam ${fotosFaltando})`
    : origemEdicao
    ? 'Salvar imagens'
    : 'Enviar Vistoria';

  /**
   * Os dois sentidos do espelho (at-32) — a regra vive em `utils/sincronizacaoSga.ts`
   * desde a at-47, porque esta função era **idêntica** à de `/carro/substituir` e o
   * defeito corrigido lá (B8) estava nas duas cópias.
   */
  const sincronizarComSga = async (acao: AcaoSincronizacaoSga, carroIdAlvo: string) => {
    if (!carroIdAlvo) return;

    setSincronizandoSga(true);
    try {
      const restantes = await sincronizarVeiculoComSga(acao, carroIdAlvo, divergenciasSga);
      setDivergenciasSga(restantes);
    } catch (erro) {
      toast.error(mensagemDeErroSincronizacao(erro));
    } finally {
      setSincronizandoSga(false);
    }
  };

  /**
   * Sem veículo no Redux não há o que fotografar — ver o item 1 do cabeçalho.
   *
   * ⚠️ É a decisão que **substitui** o `navigate()` no render. Acontece por link
   * direto, aba nova ou F5, porque o fluxo é sempre alcançado a partir do painel
   * (ou da carteira), que é quem hidrata o slice.
   */
  const semVeiculoDoFluxo = !vistoria.vehicle || !vistoria.cpf;

  return (
    <ShellSaac
      cabecalho={
        <PageHeader sobretitulo="Vistoria" titulo="Captura de imagens" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              {/*
                Bolinhas DINÂMICAS e navegáveis (pedido de Victor, 10/09/2026).
                O estado de cada etapa vem do BANCO, não do Redux, e o
                `fotosNaoEnviadas` é o que faz a navegação confirmar antes de
                descartar fotos que só existem em memória.
              */}
              <StepperVistoria etapa={1} fotosNaoEnviadas={fotosFaltando} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {semVeiculoDoFluxo ? (
                <EmptyState
                  titulo="Nenhum veículo selecionado"
                  descricao="A captura é aberta a partir de um veículo da sua carteira. Vá ao painel de vistorias, escolha o veículo e inicie a vistoria por lá."
                  icone="alerta"
                  acao={
                    <Botao variante="primario" onClick={() => navigate(VISTORIA())}>
                      Ir para o painel
                    </Botao>
                  }
                />
              ) : (
                <>
                  {/* Legado, de propósito: 4 consumidores (at-56) e o rótulo,
                      a descrição e a gravidade vêm prontos do backend. */}
                  <AvisoDivergenciaSga
                    divergencias={divergenciasSga}
                    processando={sincronizandoSga}
                    onTrazerDoSga={() =>
                      sincronizarComSga('TRAZER', String(vistoria.vehicle?.id || ''))
                    }
                    onEnviarAoSga={() =>
                      sincronizarComSga('ENVIAR', String(vistoria.vehicle?.id || ''))
                    }
                  />

                  <div className="tw-flex tw-flex-col tw-gap-6">
                    <FormSection titulo="Veículo" icone="veiculo">
                      <dl className="tw-m-0 tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
                        <Dado rotulo="CPF/CNPJ" valor={vistoria.cpf} />
                        <Dado rotulo="Placa" valor={vistoria.vehicle?.placa} />
                        <Dado rotulo="Modelo" valor={vistoria.vehicle?.modelo} />
                        <Dado rotulo="Ano modelo" valor={vistoria.vehicle?.anoModelo} />
                        <Dado rotulo="Ano fabricação" valor={vistoria.vehicle?.anoFabricacao} />
                        <Dado rotulo="Tipo de veículo" valor={vistoria.vehicle?.tipoVeiculo} />
                      </dl>
                    </FormSection>

                    {/* Rascunho retomado: fotos que já estão no banco. Não são
                        recapturadas — cada uma pode ser removida para liberar o
                        slot e refazer (o slot é da LEGENDA, nunca da posição). */}
                    {fotosEnviadas.length > 0 && (
                      <FormSection
                        titulo={`Fotos já enviadas (${fotosEnviadas.length} de ${totalCaption})`}
                        icone="vistoria"
                      >
                        <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3 md:tw-grid-cols-4">
                          {fotosEnviadas.map((foto) => (
                            <MiniaturaFotoVistoria
                              key={foto.id}
                              src={foto.url}
                              legenda={foto.caption}
                              avaria={foto.damaged}
                              acoes={
                                <Botao
                                  variante="perigo"
                                  tamanho="sm"
                                  larguraTotal
                                  carregando={removendoFoto === foto.id}
                                  disabled={removendoFoto === foto.id || saving}
                                  onClick={() => setFotoParaRemover(foto)}
                                >
                                  {removendoFoto === foto.id ? 'Removendo...' : 'Remover'}
                                </Botao>
                              }
                            />
                          ))}
                        </div>
                      </FormSection>
                    )}

                    <FormSection titulo="Capturar fotos" icone="vistoria">
                      <InspectionCapture
                        // Só os slots ainda livres: garante que cada nova foto receba a
                        // legenda do slot que está preenchendo (at-24).
                        captions={legendasVazias}
                        maxPhotos={legendasVazias.length}
                        onImagesChange={setImagesMeta}
                        onImagesWithFileChange={setImagesWithFile}
                        showFilePicker={elevado}
                      />
                    </FormSection>
                  </div>

                  {sendValidationMessage && (
                    <div
                      role="status"
                      className="tw-mt-4 tw-rounded-xl tw-border tw-border-amber-warn/40 tw-border-l-4 tw-border-l-amber-warn tw-bg-amber-bg tw-px-3 tw-py-2.5 tw-text-sm tw-font-semibold tw-text-amber-deep"
                    >
                      {sendValidationMessage}
                    </div>
                  )}

                  {/*
                    🔴 A barra de progresso vive DENTRO da mesma faixa `sticky` do
                    botão (pedido de Victor, 10/09/2026: *"deixe essa parte de
                    loading de envio sempre visível na versão mobile, não somente
                    quando desliza até o fim da tela"*).

                    Ela ficava no fim do conteúdo e **saía de vista** conforme a
                    grade de 29 fotos crescia — exatamente durante o envio, que é
                    quando ela importa. Agora a ação e o progresso dela são um
                    bloco só, colado no rodapé no mobile.

                    ⚠️ Vale para os DOIS estados: `mostrarProgresso` é
                    `saving || envioFalhou`, e é no de erro que estão o "Tentar
                    novamente (faltam N)" e o aviso de que as fotos já enviadas
                    foram preservadas.
                  */}
                  <FormActions
                    rodape={
                      mostrarProgresso ? (
                        <VistoriaUploadProgress
                          percent={percentUpload}
                          rotulo={rotuloProgresso}
                          estado={envioFalhou ? 'erro' : 'enviando'}
                          detalhe={detalheProgresso}
                        />
                      ) : undefined
                    }
                  >
                    <Botao
                      variante="primario"
                      larguraTotal
                      carregando={saving}
                      disabled={saving || !canSend}
                      onClick={() => void handleEnviarVistoria()}
                    >
                      {textoBotaoEnvio}
                    </Botao>
                  </FormActions>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legado (serve quase o app inteiro): confirmação sempre em modal, nunca
          `window.confirm` (at-24). */}
      <ModalConfirmacao
        aberto={!!fotoParaRemover}
        titulo="Remover foto"
        mensagem={`Remover a foto "${fotoParaRemover?.caption ?? ''}"?`}
        detalhe="O slot fica livre para você capturar de novo antes de concluir a vistoria."
        rotuloConfirmar="Remover"
        destrutivo
        processando={!!removendoFoto}
        onConfirmar={confirmarRemocaoFoto}
        onCancelar={() => setFotoParaRemover(null)}
      />
    </ShellSaac>
  );
};

export default VistoriaCapturaPage;
